import { CustomMetric } from '../../model'
import { GroupByBuilder, Target, SelectBuilder, TargetBuilder, DashboardPanel, GraphPanelBuilder } from '@mulesoft/monitoring-center-components'
import { Dashboard } from '@mulesoft/monitoring-center-components/lib/models/dashboard'
import { selectAvgRequestCountSum } from '@mulesoft/monitoring-center-components/lib/components/BuiltInDashboards/PreBuilt/common/select'
import { groupByEndpoint } from '@mulesoft/monitoring-center-components/lib/components/BuiltInDashboards/PreBuilt/common/groupBy'
import { tagsByResourceType } from '@mulesoft/monitoring-center-components/lib/components/BuiltInDashboards/PreBuilt/common/tags'
import { yAxesShortTwoDecimal } from '@mulesoft/monitoring-center-components/lib/components/BuiltInDashboards/PreBuilt/common/yaxes'
import { isPCE } from '../../../../../util/environments'

export const productionRP = 'rp_production'
export const nonProductionRP = 'rp_sandbox'

export const customMetricGroupByWithDimension = (dimension: string, aggregation?: string) => {
  if (aggregation && aggregation !== 'None') {
    return new GroupByBuilder()
      .pushParams(['$__interval'], 'time')
      .pushParams([dimension ? dimension : ''], 'tag')
      .build()
  } else {
    return new GroupByBuilder()
      .pushParams([dimension ? dimension : ''], 'tag')
      .build()
  }
}

export const customMetricGroupBy = (aggregation?: string) => {
  if (aggregation && aggregation !== 'None') {
    return new GroupByBuilder()
      .pushParams(['$__interval'], 'time')
      .build()
  } else {
    return new GroupByBuilder()
      .build()
  }
}

const customMetricSelectWithoutAgg = (field: string) => {
  return new SelectBuilder()
    .pushDataFunArray(new GroupByBuilder()
      .pushParams([field], 'field')
      .build())
    .build()
}

const customMetricSelectWithAgg = (field: string, agg: string) => {
  return new SelectBuilder()
    .pushDataFunArray(new GroupByBuilder()
      .pushParams([field], 'field')
      .pushParams([], agg)
      .build())
    .build()
}

const customMetricSelectEmpty = () => {
  return new SelectBuilder()
    .pushDataFunArray(new GroupByBuilder()
      .build())
    .build()
}

export const customMetricSelect = (field?: string, aggregation?: string) => {
  if (field && field !== 'None' && aggregation && aggregation !== 'None') {
    return customMetricSelectWithAgg(field, aggregation)
  }

  if (field && field !== 'None') {
    return customMetricSelectWithoutAgg(field)
  }

  return customMetricSelectEmpty()
}

export const customMetricTags = (metricName: string, orgId: string, envId: string, appId: string, rsType: string, targetId?: string) => {
  return isPCE() ? [
    ...tagsByResourceType(orgId, envId, appId, rsType, targetId),
  ] : [
    ...tagsByResourceType(orgId, envId, appId, rsType, targetId),
    {'key': 'metric_name', 'operator': '=', 'value': metricName, 'condition': 'AND'}
  ]
}

export const customMetricTarget = (metric: CustomMetric, orgId: string, envId: string, appId: string, rsType: string,
                                   targetId?: string, isProductionEnv?: boolean): Target[] => {
  const aggregation = metric.selector.aggregationSelector.selected
  const field = metric.selector.measureSelector.selected
  const dimension = metric.selector.dimensionSelector.selected

  const select = customMetricSelect(field, aggregation)
  const groupBy = (!dimension || dimension === 'None') ? customMetricGroupBy(aggregation) : customMetricGroupByWithDimension(dimension, aggregation)

  const targetBuilder = new TargetBuilder()
    .withAlias((!dimension || dimension === 'None') ? '$col' : `$tag_${dimension}`)
    .withRefId('A')
    .withGroupBy(groupBy).withSelect(select)
    .withTags(customMetricTags(metric.name, orgId, envId, appId, rsType, targetId))

  if (isPCE()) {
    targetBuilder
      .withMeasurement(metric.name)
      .withPolicy(isProductionEnv ? productionRP : nonProductionRP)
  } else {
    targetBuilder
      .withMeasurement('custom_metric')
  }

  return [targetBuilder.build()]
}

export const customMetricPanel = (index: number, metric: CustomMetric, orgId: string, envId: string, appId: string, rsType: string,
                                  targetId?: string, isProductionEnv?: boolean): DashboardPanel => {
  return new GraphPanelBuilder(index)
    .withTitle(metric.name)
    .withFill(2).withLinewidth(2).withSpan(12)
    .withTargets(customMetricTarget(metric, orgId, envId, appId, rsType, targetId, isProductionEnv)).withYaxes(yAxesShortTwoDecimal)
    .build()
}

export const buildCustomMetricDashboard = (metric: CustomMetric, orgId: string, envId?: string, appId?: string, rsType?: string,
                                           targetId?: string, isProductionEnv?: boolean) => {
  return {
    rows: [
      {
        height: '270px', panels: [customMetricPanel(0, metric, orgId, envId || '', appId || '', rsType || '', targetId, isProductionEnv)]
      }
    ],
    isCustomMetric: true
  }
}

export const buildHiddenCustomMetricDashboard = (orgId: string, envId?: string, appId?: string, rsType?: string,
                                                 targetId?: string): Dashboard => {
  return {
    rows: [
      {
        height: '270px', panels: [hiddenCustomMetricPanel(orgId, envId || '', appId || '', rsType || '', targetId)]
      }
    ]
  }
}

export const hiddenCustomMetricPanel = (orgId: string, envId: string, appId: string, rsType: string,
                                        targetId?: string): DashboardPanel => {
  return new GraphPanelBuilder(0)
    .withTitle('')
    .withFill(3).withLinewidth(2).withSpan(12)
    .withTargets(hiddenCustomMetricTarget(orgId, envId, appId, rsType, targetId)).withYaxes(yAxesShortTwoDecimal)
    .build()
}

export const hiddenCustomMetricTarget = (orgId: string, envId: string, appId: string, rsType: string, targetId?: string): Target[] => {
  return [
    new TargetBuilder()
      .withAlias('$tag_endpoint').withMeasurement('app_inbound_metric').withRefId('A')
      .withGroupBy(groupByEndpoint()).withSelect(selectAvgRequestCountSum())
      .withTags(tagsByResourceType(orgId, envId, appId, rsType, targetId))
      .build()
  ]
}

export const buildCompleteCustomMetricsDashboard = (metrics: CustomMetric[], orgId: string, envId?: string, appId?: string, rsType?: string,
                                                    targetId?: string, isProductionEnv?: boolean): Dashboard => {
  const rows = metrics.map((metric, index) => {
    const panel = customMetricPanel(index + 1, metric, orgId, envId || '', appId || '', rsType || '', targetId, isProductionEnv)
    const aggregation = metric.selector.aggregationSelector.selected

    if (
      panel.targets.length > 0 &&
      panel.targets[0].select.length > 0 &&
      panel.targets[0].select[0].length === 0 &&
      metric.selector.measureSelector.measures &&
      metric.selector.measureSelector.measures.length > 1
    ) {
      panel.targets[0].select = aggregation && aggregation !== 'None' ?
        customMetricSelectWithAgg(metric.selector.measureSelector.measures[1], aggregation) :
        customMetricSelectWithoutAgg(metric.selector.measureSelector.measures[1])
    }

    return {
      height: '270',
      panels: [panel]
    }
  })

  return {
    rows: rows
  }
}

export const buildCustomMetricsKey = (metrics: CustomMetric[]) => {
  let key = ''

  metrics.forEach(metric => {
    const metricName = metric.name || ''
    const metricNameKey = metricName ? '-' + metricName : metricName
    const aggregation = metric.selector.aggregationSelector.selected || ''
    const aggregations = metric.selector.aggregationSelector.aggregations ? metric.selector.aggregationSelector.aggregations.length : 0
    const aggregationKey = aggregation ? '-' + aggregation + '-' + aggregations : aggregation + '-' + aggregations
    const dimension = metric.selector.dimensionSelector.selected || ''
    const dimensions = metric.selector.dimensionSelector.dimensions ? metric.selector.dimensionSelector.dimensions.length : 0
    const dimensionKey = dimension ? '-' + dimension + '-' + dimensions : dimension + '-' + dimensions
    const measure = metric.selector.measureSelector.selected || ''
    const measures = metric.selector.measureSelector.measures ? metric.selector.measureSelector.measures.length : 0
    const measureKey = measure ? '-' + measure + '-' + measures : measure + '-' + measures

    key += `${metricNameKey}${aggregationKey}${dimensionKey}${measureKey}`
  })

  return metrics.length > 0 ? key.slice(1) : key
}

export const styles = () => [
  {
    cssSelector: '.builtin-dashboard-container',
    properties: {padding: 0}
  },
  {
    cssSelector: '.builtin-dashboard-container .panel-container',
    properties: {border: 'none'}
  },
  {
    cssSelector: '.builtin-dashboard-container plugin-component.panel-margin',
    properties: {marginLeft: 0, marginRight: 0}
  },
  {
    cssSelector: 'body',
    properties: {overflowY: 'hidden' as 'hidden'}
  },
  {
    cssSelector: '.panel',
    properties: {background: 'white'}
  },
  {
    cssSelector: '.panel-header.with-message',
    properties: {marginBottom: '20px !important'}
  },
  {
    cssSelector: '.panel-messages',
    properties: {display: 'none'}
  },
  {
    cssSelector: '.alert-warning.alert',
    properties: {display: 'none'}
  }
]
